import { useCallback, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * Tracks internal navigation history to prevent back-button loops.
 * Stores a stack of visited paths within the app.
 */
const internalHistory = [];
let listenerAttached = false;

/**
 * useSafeBack — replaces navigate(-1) with loop-safe navigation.
 *
 * @param {string} fallback — route to use if there's no valid internal page to go back to.
 *                             Defaults to '/dashboard'.
 * @returns {{ goBack: () => void, canGoBack: boolean }}
 */
export function useSafeBack(fallback = '/dashboard') {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = useRef(location.pathname);

  // Track every route change in the internal history stack
  useEffect(() => {
    const path = location.pathname;
    // Only push if it's different from the current top (avoid duplicates from re-renders)
    if (internalHistory[internalHistory.length - 1] !== path) {
      internalHistory.push(path);
    }
    currentPath.current = path;
  }, [location.pathname]);

  const goBack = useCallback(() => {
    // Pop current page off the stack
    if (internalHistory.length > 0 && internalHistory[internalHistory.length - 1] === currentPath.current) {
      internalHistory.pop();
    }

    // Find a valid previous page that isn't the current page (prevents A→B→A→B loops)
    let target = null;
    while (internalHistory.length > 0) {
      const prev = internalHistory[internalHistory.length - 1];
      if (prev !== currentPath.current) {
        target = prev;
        break;
      }
      internalHistory.pop(); // Skip duplicates
    }

    if (target) {
      navigate(target);
    } else {
      // No valid internal history — go to the safe fallback
      navigate(fallback);
    }
  }, [navigate, fallback]);

  // There's a valid back destination if the stack has entries besides the current page
  const canGoBack = internalHistory.length > 1;

  return { goBack, canGoBack };
}
